import React, { useEffect, useState, ReactNode } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import { SxProps, useTheme } from '@mui/material/styles'
import { geocodeByAddress } from 'react-places-autocomplete'
import { Controller, useForm, UseFormReturn } from 'react-hook-form'
import TextInput from '../../../base/TextInput/TextInput'
import { validators } from '../../../utils/validators'

//Note: non-sensitive
const GOOGLE_PLACES_API_KEY = 'AIzaSyC6GVrzTWJ5ujsB0XguJVGtkoryFpQHxm4'
const GOOGLE_MAPS_API_URL = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places&types=address&loading=async`

const defaultSx = (theme: any) => {
  return {
    autocompleteStyle: {
      '& .MuiAutocomplete-inputRoot': {
        flexWrap: 'nowrap',
      },
      '& .MuiAutocomplete-input': {
        padding: '0 !important',
      },
    },
    resultsContainerStyle: {
      '.MuiPaper-root.MuiAutocomplete-paper': {
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: `0px 1px 6px 0px ${theme.palette.primary.main}`,
        borderRadius: '8px',
      },
    },
  }
}

export interface PlacesAutocompleteProps {
  countryCode?: string
  defaultValue?: string
  form?: UseFormReturn
  id?: string
  label?: string
  name: string
  onSelectPlaceCallback: Function
  required?: boolean
  rules?: object
  sx?: SxProps
  endAdornment?: ReactNode
}

class Option {
  description: string
  placeId: string

  constructor(option: any = {}) {
    this.description = option.description
    this.placeId = option.place_id
  }
}

export class Place {
  placeId: string
  fullAddress: string
  line1: string
  city: string
  state: string
  postalCode: string
  country: string

  constructor(place: any = {}) {
    this.placeId = place.placeId
    this.fullAddress = place.fullAddress
    this.line1 = place.line1
    this.city = place.city
    this.state = place.state
    this.postalCode = place.postalCode
    this.country = place.country
  }
}

const PlacesAutocomplete = ({
  countryCode,
  defaultValue,
  form,
  id,
  label,
  name,
  onSelectPlaceCallback,
  required,
  rules,
  sx,
  endAdornment,
}: PlacesAutocompleteProps) => {
  const [autocompleteService, setAutocompleteService] = useState<any>({
    loaded: false,
    instance: null,
  })

  useEffect(() => {
    const onLoadScript = () => {
      if (window.google && !autocompleteService.loaded) {
        setAutocompleteService({
          loaded: true,
          instance: new window.google.maps.places.AutocompleteService(),
        })
      }
    }
    if (!document.querySelector('#google-maps')) {
      const script = document.createElement('script')
      script.setAttribute('id', 'google-maps')
      script.src = GOOGLE_MAPS_API_URL
      document.querySelector('head')?.appendChild(script)
    } else {
      onLoadScript()
    }

    document.addEventListener('readystatechange', () => {
      if (document.readyState === 'complete') {
        onLoadScript()
      }
    })
  }, [window.google])

  const theme = useTheme()
  const { autocompleteStyle, resultsContainerStyle } = defaultSx(theme)

  const [textInputValue, setTextInputValue] = useState('')
  const [placeOptions, setPlaceOptions] = useState<Array<Option>>([])
  const [selectedPlace, setSelectedPlace] = useState<Place>(new Place())

  const useFormDefault = form || useForm()
  const { control } = useFormDefault

  const handleInputChange = (value: string) => {
    setTextInputValue(value)
    getPlacePredictions(value)
  }

  const getPlacePredictions = (searchString: string) => {
    if (autocompleteService.instance && searchString) {
      const request = {
        input: searchString,
        ...(countryCode && {
          componentRestrictions: {
            country: [countryCode],
          },
        }),
      }

      autocompleteService.instance.getPlacePredictions(
        request,
        (results: []) => {
          const options = results?.map(option => new Option(option)) || []
          setPlaceOptions(options)
        },
      )
    }
  }

  const parseGeocoderResult = (geocoderResult: google.maps.GeocoderResult) => {
    const findAddressComponent = (type: string) =>
      geocoderResult.address_components?.find(
        c => c.types.find(Boolean) === type,
      )

    return {
      fullAddress: geocoderResult.formatted_address,
      line1: geocoderResult.formatted_address?.split(',')?.find(Boolean),
      city: findAddressComponent('locality')?.long_name,
      state: findAddressComponent('administrative_area_level_1')?.short_name,
      postalCode: findAddressComponent('postal_code')?.short_name,
      country: findAddressComponent('country')?.short_name,
    }
  }

  const handleSelectPlace = (placeOption: Option) => {
    if (placeOption?.description) {
      geocodeByAddress(placeOption.description)
        .then((results: google.maps.GeocoderResult[]) => {
          if (results?.length) {
            const place = results.map(parseGeocoderResult).find(Boolean)
            const selectedPlace = new Place({
              placeId: placeOption.placeId,
              ...place,
            })
            setSelectedPlace(selectedPlace)
            onSelectPlaceCallback(selectedPlace)
          }
        })
        .catch(() => console.log('google geocode error'))
    }
  }

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue || ''}
      render={({ field: { onBlur, onChange, value } }) => (
        <Autocomplete
          autoHighlight={false}
          componentsProps={{
            popper: {
              sx: {
                ...resultsContainerStyle,
                visibility: value ? 'initial' : 'hidden',
              },
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5],
                  },
                },
              ],
            },
          }}
          disableClearable={true}
          freeSolo={true}
          getOptionLabel={optionOrInputValue =>
            typeof optionOrInputValue === 'string'
              ? optionOrInputValue
              : optionOrInputValue?.description || ''
          }
          id={id || name}
          inputValue={textInputValue || ''}
          isOptionEqualToValue={option => {
            return option?.placeId && selectedPlace.placeId === option.placeId
          }}
          ListboxComponent={React.forwardRef(
            (props, ref: React.ForwardedRef<HTMLUListElement>) => {
              return (
                <ul {...props} ref={ref}>
                  {props.children}
                </ul>
              )
            },
          )}
          onBlur={onBlur}
          onChange={(event, option) => {
            handleSelectPlace(option)
            onChange(event)
          }}
          onFocus={() => {
            getPlacePredictions(textInputValue)
          }}
          onInputChange={(event, value) => {
            handleInputChange(value)
          }}
          options={placeOptions}
          renderInput={({ InputLabelProps, InputProps, inputProps }) => (
            <TextInput
              form={useFormDefault}
              InputLabelProps={InputLabelProps}
              InputProps={{
                ...InputProps,
                endAdornment: !!value && endAdornment,
              }}
              inputProps={{
                ...inputProps,
                //Note: prevent browser autofill suggestions
                autoComplete: 'new-password',
              }}
              label={label}
              name={name}
              required={required}
              rules={{
                addressLine: validators.validateAddress,
                ...rules,
              }}
              sx={sx}
            />
          )}
          sx={autocompleteStyle}
          value={value}
        />
      )}
    />
  )
}

export default PlacesAutocomplete
